import { useState } from 'react';

import type {
  TicketDependencies,
  TicketDependencyEdge,
  TicketDetail,
  UiHoldOffer,
} from '@nightshift/ui-types';

import { api, ApiError } from '../../live/api.ts';
import { useTimeFormat } from '../../lib/display.ts';
import { observedTime } from '../../lib/format.ts';
import { FootprintSection } from './footprint.tsx';
import { SuggestionsPanel } from './suggestions.tsx';
import './dependencies.css';

/**
 * Confirmed dependency edges, both directions.
 *
 * "Depends on" and "blocks" are relationships; "waiting for" is a ticket state. The two are
 * kept apart in the wording on purpose: a ticket can depend on three things and be parked on
 * none of them, and an operator reading "waiting" on a Backlog card would go looking for a
 * Waiting row that does not exist.
 */
export function DependenciesTab({
  detail,
  now,
  onReloaded,
  onPreviewTicket,
}: {
  detail: TicketDetail;
  now: number;
  onReloaded: () => Promise<void> | void;
  onPreviewTicket?: (key: string) => void;
}) {
  const deps: TicketDependencies = detail.dependencies;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState('');
  const [hold, setHold] = useState<UiHoldOffer | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function run(work: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await work();
      await onReloaded();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const add = () => {
    const blocker = adding.trim();
    if (!blocker) return;
    void run(async () => {
      // The link is recorded whatever happens next. `hold` comes back only when the ticket
      // was already moving, which is the case the Start gate can never see: NS-5's dependency
      // on NS-4 was written eight milliseconds after NS-5 started.
      const result = await api.addDependency(detail.ticket.ticketKey, { blocker });
      setAdding('');
      setHold(result.hold ?? null);
      setNotice(result.impliedByMilestoneOrder ? `Not needed: ${result.impliedByMilestoneOrder}.` : null);
    });
  };

  return (
    <section className="deps" aria-label="Dependencies">
      {error ? <p className="deps-error">{error}</p> : null}
      {notice ? <p className="dim" role="status">{notice}</p> : null}

      {hold ? (
        <HoldOfferRow
          ticketKey={detail.ticket.ticketKey}
          offer={hold}
          busy={busy}
          onDismiss={() => setHold(null)}
          onHold={() =>
            void run(async () => {
              await api.wait(detail.ticket.ticketKey, {
                reason_type: 'dependency',
                reason: `held until ${hold.blockerKey} lands`,
                wake_mode: 'automatic',
                condition_ref: String(hold.blockerTicketId),
              });
              setHold(null);
            })
          }
        />
      ) : null}

      <div className="deps-add">
        <label className="deps-add-label" htmlFor="deps-add-input">
          {detail.ticket.ticketKey} depends on
        </label>
        <div className="deps-add-row">
          <input
            id="deps-add-input"
            className="input mono"
            placeholder="TICKET-KEY"
            value={adding}
            disabled={busy}
            onChange={(event) => setAdding(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                add();
              }
            }}
          />
          <button
            type="button"
            className="btn btn-sm"
            disabled={busy || adding.trim().length === 0}
            onClick={add}
          >
            Add
          </button>
        </div>
        <p className="deps-add-hint">
          The ticket you add must finish first. Same project only; dependency loops are refused.
        </p>
      </div>

      <EdgeList
        heading={`${detail.ticket.ticketKey} depends on`}
        empty="No confirmed prerequisites."
        edges={deps.dependsOn}
        now={now}
        busy={busy}
        direction="blocker"
        ticketKey={detail.ticket.ticketKey}
        onPreviewTicket={onPreviewTicket}
        onRemove={(edge) =>
          void run(() => api.removeDependency(detail.ticket.ticketKey, edge.ticketKey))
        }
      />

      <EdgeList
        heading={`Tickets that depend on ${detail.ticket.ticketKey}`}
        empty="No confirmed tickets depend on this one."
        edges={deps.blocks}
        now={now}
        busy={busy}
        direction="dependent"
        ticketKey={detail.ticket.ticketKey}
        onPreviewTicket={onPreviewTicket}
        // Removing from this side would edit the *other* ticket's dependencies. That is its
        // own decision, made on its own card, so this list is read-only.
        onRemove={undefined}
      />

      <FootprintSection
        showCategory={false}
        suggestions={detail.suggestions}
        ticketKey={detail.ticket.ticketKey}
        ticketVersion={detail.ticket.version}
        now={now}
        onReloaded={async () => { await onReloaded(); }}
      />

      <SuggestionsPanel
        ticketKey={detail.ticket.ticketKey}
        suggestions={detail.suggestions}
        now={now}
        onReloaded={onReloaded}
        onPreviewTicket={onPreviewTicket}
      />
    </section>
  );
}

const CHIP_LABEL: Record<string, string> = {
  blocked: 'not done',
  satisfied: 'done',
  stale: 'not moved in a fortnight',
  unavailable: 'cancelled',
  missing: 'unknown',
};

function EdgeList({
  heading,
  empty,
  edges,
  now,
  busy,
  direction,
  ticketKey,
  onPreviewTicket,
  onRemove,
}: {
  heading: string;
  empty: string;
  edges: TicketDependencyEdge[];
  now: number;
  busy: boolean;
  direction: 'blocker' | 'dependent';
  ticketKey: string;
  onPreviewTicket?: (key: string) => void;
  onRemove?: (edge: TicketDependencyEdge) => void;
}) {
  const format = useTimeFormat();
  return (
    <div className="deps-group">
      <h3 className="deps-heading">
        {heading}
        {edges.length > 0 ? <span className="dim num"> {edges.length}</span> : null}
      </h3>
      {edges.length === 0 ? (
        <p className="deps-empty">{empty}</p>
      ) : (
        <ul className="deps-list">
          {edges.map((edge) => (
            <li key={`${direction}-${edge.ticketKey}`} className={`deps-row is-${edge.chip}`}>
              <span className="deps-order">
                {direction === 'blocker'
                  ? `${ticketKey} depends on ${edge.ticketKey}`
                  : `${edge.ticketKey} depends on ${ticketKey}`}
              </span>
              {edge.sameProject && onPreviewTicket ? (
                <button
                  type="button"
                  className="deps-key mono"
                  onClick={() => onPreviewTicket(edge.ticketKey)}
                  aria-label={`Preview ${edge.ticketKey}: ${edge.title}`}
                >
                  {edge.ticketKey}
                </button>
              ) : (
                <span className="deps-key mono">{edge.ticketKey}</span>
              )}
              <span className="deps-title clamp-1">{edge.title}</span>
              <span className={`chip deps-chip deps-chip-${edge.chip}`}>
                {edge.state}
                {edge.chip !== 'blocked'
                  ? ` · ${direction === 'dependent' ? `${ticketKey}: ` : ''}${CHIP_LABEL[edge.chip] ?? edge.chip}`
                  : ''}
              </span>
              {!edge.sameProject ? (
                <span className="chip deps-foreign" title="Recorded before cross-project links were refused">
                  other project
                </span>
              ) : null}
              {edge.automatic ? <span className="chip" title={edge.reason}>Automatically added</span> : null}
              {edge.reason ? <p className="deps-order dim">{edge.reason}</p> : null}
              <span className="deps-since dim">{observedTime(edge.since, now, format)}</span>
              {onRemove ? (
                <button
                  type="button"
                  className="btn btn-sm deps-remove"
                  disabled={busy}
                  onClick={() => onRemove(edge)}
                >
                  {edge.automatic ? 'Undo automatic dependency' : 'Remove'}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The offer that follows a dependency recorded against a ticket that is already moving.
 *
 * Not a warning and not automatic. Holding an attempt that is two minutes from a merge costs
 * more than the rebase would, and the person who just typed the blocker's key is the only one
 * who knows which case this is. So the row states both facts and offers one button.
 *
 * The hold itself is the ordinary dependency wait, on automatic wake: whatever is running
 * stops, and the ticket comes back on its own when the blocker reaches Done.
 */
function HoldOfferRow({
  ticketKey,
  offer,
  busy,
  onHold,
  onDismiss,
}: {
  ticketKey: string;
  offer: UiHoldOffer;
  busy: boolean;
  onHold: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="deps-hold" role="status">
      <p className="deps-hold-text">
        {ticketKey} is in {offer.state}
        {offer.attemptId ? ' with an attempt running' : ''}, and {offer.blockerKey} is{' '}
        {offer.blockerState}. Hold it until {offer.blockerKey} lands, or let this run finish and
        rebase later.
      </p>
      <div className="deps-hold-actions">
        <button type="button" className="btn btn-sm btn-accent" disabled={busy} onClick={onHold}>
          Hold until {offer.blockerKey} lands
        </button>
        <button type="button" className="btn btn-sm" disabled={busy} onClick={onDismiss}>
          Keep going
        </button>
      </div>
    </div>
  );
}
